import type { SessionState, LayoutNode, TabType, TabEntry } from './types';

// ---------------------------------------------------------------------------
// openTab
// ---------------------------------------------------------------------------

export function openTab(
  state: SessionState,
  entityId: string,
  tabType: TabType,
  panelId?: string,
): SessionState {
  // If this entity is ALREADY open in any panel, focus that tab there instead
  // of opening a duplicate. (Prevents two tabs for the same entity across panels,
  // which would allow concurrent same-conversation turns — see M5 concurrency safety.)
  for (const p of Object.values(state.panels)) {
    const existing = p.tabs.find((t) => t.id === entityId);
    if (existing) {
      // Correct the type if a deep-link assumed the wrong one — EXCEPT a
      // conversation tab is authoritative (its type is minted at creation and
      // has no entity Yjs room to recover from). A typeless re-open on
      // switch-back resolves to the 'document' default; clobbering the tab to
      // 'document' subscribes it to a non-existent document room → perpetual
      // loading. Keep conversation tabs as conversations across switch-away/back.
      // Primary fix: tab-bar.tsx handleClick now passes 'conversation' directly;
      // this guard is defense-in-depth for any caller that omits the type.
      const tabs =
        existing.type !== tabType && existing.type !== 'conversation'
          ? p.tabs.map((t) => (t.id === entityId ? { ...t, type: tabType } : t))
          : p.tabs;
      return {
        ...state,
        focusedPanelId: p.panelId,
        panels: {
          ...state.panels,
          [p.panelId]: { ...p, tabs, activeId: entityId },
        },
      };
    }
  }

  // Not open anywhere — add it to the requested (or focused) panel.
  const targetPanelId = panelId ?? state.focusedPanelId;
  const panel = state.panels[targetPanelId];
  if (!panel) return state;
  return {
    ...state,
    panels: {
      ...state.panels,
      [targetPanelId]: {
        ...panel,
        tabs: [...panel.tabs, { id: entityId, type: tabType }],
        activeId: entityId,
      },
    },
  };
}

// ---------------------------------------------------------------------------
// closeTab
// ---------------------------------------------------------------------------

export function closeTab(
  state: SessionState,
  entityId: string,
  panelId: string,
): SessionState {
  const panel = state.panels[panelId];
  if (!panel) return state;
  if (!panel.tabs.some((t) => t.id === entityId)) return state;

  const newTabs = panel.tabs.filter((t) => t.id !== entityId);

  if (newTabs.length === 0) {
    // Close the panel — collapses to default if it was the only one
    return closePanel(state, panelId);
  }

  let newActiveId = panel.activeId;
  if (panel.activeId === entityId) {
    const closedIndex = panel.tabs.findIndex((t) => t.id === entityId);
    // Try the tab at the same index (now the next tab); fall back to previous
    newActiveId = (newTabs[closedIndex] ?? newTabs[closedIndex - 1] ?? newTabs[0]).id;
  }

  return {
    ...state,
    panels: {
      ...state.panels,
      [panelId]: { ...panel, tabs: newTabs, activeId: newActiveId },
    },
  };
}

// ---------------------------------------------------------------------------
// moveTab
// ---------------------------------------------------------------------------

export function moveTab(
  state: SessionState,
  entityId: string,
  fromPanelId: string,
  toPanelId: string,
  atIndex?: number,
): SessionState {
  const fromPanel = state.panels[fromPanelId];
  const toPanel = state.panels[toPanelId];
  if (!fromPanel || !toPanel) return state;

  const movingEntry = fromPanel.tabs.find((t) => t.id === entityId);
  if (!movingEntry) return state;

  const newFromTabs = fromPanel.tabs.filter((t) => t.id !== entityId);

  // Determine new activeId for source panel
  let newFromActiveId = fromPanel.activeId;
  if (fromPanel.activeId === entityId && newFromTabs.length > 0) {
    const closedIndex = fromPanel.tabs.findIndex((t) => t.id === entityId);
    newFromActiveId = (newFromTabs[closedIndex] ?? newFromTabs[closedIndex - 1] ?? newFromTabs[0]).id;
  }

  // Add to target panel at the specified index, or append if not specified
  const alreadyInTarget = toPanel.tabs.some((t) => t.id === entityId);
  let newToTabs: typeof toPanel.tabs;
  if (alreadyInTarget) {
    newToTabs = toPanel.tabs;
  } else if (atIndex !== undefined) {
    const clamped = Math.max(0, Math.min(atIndex, toPanel.tabs.length));
    newToTabs = [...toPanel.tabs.slice(0, clamped), movingEntry, ...toPanel.tabs.slice(clamped)];
  } else {
    newToTabs = [...toPanel.tabs, movingEntry];
  }

  let newState: SessionState = {
    ...state,
    panels: {
      ...state.panels,
      [fromPanelId]: { ...fromPanel, tabs: newFromTabs, activeId: newFromActiveId },
      [toPanelId]: { ...toPanel, tabs: newToTabs, activeId: entityId },
    },
  };

  // If source panel is now empty, remove it
  if (newFromTabs.length === 0) {
    newState = closePanel(newState, fromPanelId);
  }

  return newState;
}

// ---------------------------------------------------------------------------
// reorderTab
// ---------------------------------------------------------------------------

export function reorderTab(
  state: SessionState,
  entityId: string,
  panelId: string,
  newIndex: number,
): SessionState {
  const panel = state.panels[panelId];
  if (!panel) return state;

  const oldIndex = panel.tabs.findIndex((t) => t.id === entityId);
  if (oldIndex === -1) return state;

  const tabs = [...panel.tabs];
  const [entry] = tabs.splice(oldIndex, 1);
  const clampedIndex = Math.max(0, Math.min(newIndex, tabs.length));
  tabs.splice(clampedIndex, 0, entry);

  return {
    ...state,
    panels: {
      ...state.panels,
      [panelId]: { ...panel, tabs },
    },
  };
}

// ---------------------------------------------------------------------------
// focusPanel
// ---------------------------------------------------------------------------

export function focusPanel(state: SessionState, panelId: string): SessionState {
  if (!state.panels[panelId]) return state;
  return { ...state, focusedPanelId: panelId };
}

// ---------------------------------------------------------------------------
// openPanel
// ---------------------------------------------------------------------------

export function openPanel(
  state: SessionState,
  direction: 'h' | 'v',
  initialDocId?: string,
): SessionState {
  const newPanelId = crypto.randomUUID();
  const initialTab: TabEntry = initialDocId
    ? { id: initialDocId, type: 'document' }
    : { id: 'home', type: 'view' };
  const newPanel = {
    panelId: newPanelId,
    activeId: initialTab.id,
    tabs: [initialTab],
  };

  const newPanelNode: LayoutNode = { type: 'panel', panelId: newPanelId };

  let newLayout: LayoutNode;
  if (state.layout.type === 'split' && state.layout.direction === direction) {
    // Same direction — append to existing split
    newLayout = {
      ...state.layout,
      children: [...state.layout.children, newPanelNode],
    };
  } else if (state.layout.type === 'split') {
    // Different direction — wrap the focused panel + new panel in a nested split
    const focusedNode = state.layout.children.find(
      (c) => c.type === 'panel' && c.panelId === state.focusedPanelId,
    );
    if (focusedNode) {
      const nestedSplit: LayoutNode = {
        type: 'split',
        direction,
        children: [focusedNode, newPanelNode],
      };
      newLayout = {
        ...state.layout,
        children: state.layout.children.map((c) =>
          c === focusedNode ? nestedSplit : c,
        ),
      };
    } else {
      // Fallback — append anyway
      newLayout = {
        ...state.layout,
        children: [...state.layout.children, newPanelNode],
      };
    }
  } else {
    // Single panel — wrap in a split
    newLayout = {
      type: 'split',
      direction,
      children: [state.layout, newPanelNode],
    };
  }

  return {
    ...state,
    layout: newLayout,
    panels: {
      ...state.panels,
      [newPanelId]: newPanel,
    },
    focusedPanelId: newPanelId,
  };
}

// ---------------------------------------------------------------------------
// closePanel
// ---------------------------------------------------------------------------

export function closePanel(state: SessionState, panelId: string): SessionState {
  if (!state.panels[panelId]) return state;

  // Remove from panels record
  const newPanels = { ...state.panels };
  delete newPanels[panelId];

  // Remove from layout tree
  const newLayout = removePanelFromLayout(state.layout, panelId);

  // If layout is null (removed the only panel), create default home state
  if (newLayout === null) {
    const defaultPanelId = crypto.randomUUID();
    return {
      layout: { type: 'panel', panelId: defaultPanelId },
      panels: {
        [defaultPanelId]: {
          panelId: defaultPanelId,
          activeId: 'home',
          tabs: [{ id: 'home', type: 'view' }],
        },
      },
      focusedPanelId: defaultPanelId,
    };
  }

  // Update focusedPanelId if the closed panel was focused
  let newFocusedPanelId = state.focusedPanelId;
  if (state.focusedPanelId === panelId) {
    const remainingPanelIds = Object.keys(newPanels);
    newFocusedPanelId = remainingPanelIds[0] ?? '';
  }

  return {
    ...state,
    layout: newLayout,
    panels: newPanels,
    focusedPanelId: newFocusedPanelId,
  };
}

// ---------------------------------------------------------------------------
// closeEntityAcrossAllPanels
// ---------------------------------------------------------------------------

/**
 * Remove a tab by entity id from every panel it appears in.
 * Uses the existing closeTab reducer per panel so activeId selection and
 * empty-panel collapse are handled consistently.
 * Returns the same state reference unchanged when the entity is not open anywhere.
 */
export function closeEntityAcrossAllPanels(
  state: SessionState,
  entityId: string,
): SessionState {
  const panelIds = Object.keys(state.panels).filter((pid) =>
    state.panels[pid].tabs.some((t) => t.id === entityId),
  );
  if (panelIds.length === 0) return state;
  let next = state;
  for (const panelId of panelIds) {
    next = closeTab(next, entityId, panelId);
  }
  return next;
}

/**
 * Recursively remove a panel node from the layout tree.
 * Returns null if the removed node was the only node.
 * Collapses split nodes with one child back to that child.
 */
function removePanelFromLayout(layout: LayoutNode, panelId: string): LayoutNode | null {
  if (layout.type === 'panel') {
    return layout.panelId === panelId ? null : layout;
  }

  // Split node
  const newChildren: LayoutNode[] = [];
  for (const child of layout.children) {
    const result = removePanelFromLayout(child, panelId);
    if (result !== null) newChildren.push(result);
  }

  if (newChildren.length === 0) return null;
  if (newChildren.length === 1) return newChildren[0];

  return { ...layout, children: newChildren };
}

// ---------------------------------------------------------------------------
// validateAndPrune
// ---------------------------------------------------------------------------

export function validateAndPrune(
  state: SessionState,
  existingDocIds: Set<string>,
): SessionState {
  const newPanels: SessionState['panels'] = {};

  for (const [id, panel] of Object.entries(state.panels)) {
    // Keep tabs that are either named views (no entity backing), conversation
    // tabs (exempt by type — a freshly-minted UUID has no DB row yet and is
    // not in existingDocIds; we preserve it so an unsent tab isn't pruned on
    // bootstrap), or tabs whose id maps to a live workspace entity.
    //
    // Runtime deletion: a conversation DELETED entity event fires _onEntityRemoved
    // (entity-store.ts), which dispatches closeEntityAcrossAllPanels via the
    // setEntityRemovedCallback registered in session-context.tsx. So a tab whose
    // conversation row is deleted while the app is open IS closed at runtime.
    //
    // Bootstrap gap: the prune exemption means a tab whose conversation was deleted
    // while the app was closed will NOT be pruned on next launch — it survives as a
    // stale tab until the next DELETED entity event arrives (typically the first
    // sync burst). This is a known minor gap; full cleanup requires a server-side
    // conversation-ids bootstrap feed (future work).
    const validTabs = panel.tabs.filter(
      (t) => t.type === 'view' || t.type === 'conversation' || existingDocIds.has(t.id),
    );

    // If all tabs were pruned, add home
    const finalTabs: TabEntry[] = validTabs.length > 0 ? validTabs : [{ id: 'home', type: 'view' }];

    // Fix activeId if it was pruned
    let newActiveId = panel.activeId;
    if (!finalTabs.some((t) => t.id === newActiveId)) {
      newActiveId = finalTabs[0].id;
    }

    newPanels[id] = { ...panel, tabs: finalTabs, activeId: newActiveId };
  }

  return {
    ...state,
    panels: newPanels,
  };
}

/**
 * Record which surface an entity's tab is showing, so a restored session
 * reopens the page the user left. Returns `state` unchanged when the entity
 * has no tab — the caller is a mounted view, which can outlive its tab.
 */
export function setTabSurface(
  state: SessionState,
  entityId: string,
  surfaceId: string | null,
): SessionState {
  for (const p of Object.values(state.panels)) {
    const tab = p.tabs.find((t) => t.id === entityId);
    if (!tab) continue;
    if ((tab.surfaceId ?? null) === surfaceId) return state;

    const tabs = p.tabs.map((t) => {
      if (t.id !== entityId) return t;
      if (surfaceId === null) {
        const { surfaceId: _dropped, ...rest } = t;
        return rest;
      }
      return { ...t, surfaceId };
    });

    return {
      ...state,
      panels: { ...state.panels, [p.panelId]: { ...p, tabs } },
    };
  }
  return state;
}
